import { useRef, useState } from "react";
import avatar from "assets/images/default.webp";
import { useQueryClient } from "react-query";
import { Drawer, Dropdown } from "antd";
import { SideBar } from "../sidebar/Sidebar";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { openNotification } from "utils/notification";
import { useGetTeams } from "hooks/useGetTeams";
import { useSwitchTeams } from "hooks/useSwitchTeams";
import { ISwitchTeamsProps } from "types";
import {
  useGetNotifications,
  QUERY_KEY_FOR_NOTIFICATION,
} from "../hooks/useGetNotifications";
import { useMarkNotificationRead } from "../hooks/useMarkNotificationRead";
import { useMarkAllNotificationsRead } from "../hooks/useMarkAllNotificationsRead";
import { useGetFeedback } from "../hooks/useGetFeedback";
import { ReviewForm } from "features/invites/components/ReviewForm";
import { useNavigate } from "react-router-dom";
import { routePath } from "config/routeMgt/routePath";
import dayjs from "dayjs";
import { useSignOut } from "react-auth-kit";
import WarningModal from "components/layout/components/WarningModal";

interface navProps {
  title: string;
  description: string;
}

export const Navbar = ({ description, title }: navProps) => {
  const signOut = useSignOut();

  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { userInfo, token } = useGetUserInfo();
  const { data: teams, isLoading: loading } = useGetTeams();
  const { data: notifications } = useGetNotifications();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [notData, setNotData] = useState(null);
  const { mutate, isLoading } = useSwitchTeams();
  const { mutate: markAsRead } = useMarkNotificationRead();
  const { mutate: markAllNotificationsRead, isLoading: isMarkingAllRead } =
    useMarkAllNotificationsRead();
  const { data: feedback } = useGetFeedback();
  const navigate = useNavigate();
  const [isLogoutModalVisible, setLogoutModalVisible] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);

  const tourRef = useRef<any>(null);
  const recTourRef = useRef<any>(null);

  const extractLinkFromMessage = (message: string | null | undefined) => {
    if (typeof message !== "string" || !message) {
      return null;
    }
    const regex = /(http(?:s)?:\/\/[^\s<]+)/;
    const match = message.match(regex);
    return match ? match[0] : null;
  };

  const markNotificationAsRead = (notification: any) => {
    markAsRead(
      { id: notification.id, token },
      {
        onError: () => {},
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEY_FOR_NOTIFICATION]);
        },
      },
    );
  };

  const handleMarkAllAsRead = () => {
    if (!token || isMarkingAllRead) return;

    markAllNotificationsRead(
      { token },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEY_FOR_NOTIFICATION]);
        },
        onError: () => {
          openNotification({
            title: "Error",
            state: "error",
            description: "Failed to mark notifications as read",
            duration: 4.5,
          });
        },
      },
    );
  };

  const handleStartTour = () => {
    // start();

    if (userInfo?.type === "talent") {
      tourRef.current?.start("talent");
    } else {
      recTourRef.current?.start("recruiter");
    }
  };

  let role = "";
  const isTalent = !!userInfo?.talent;
  const isRecruiter = !!userInfo?.recruiter;

  if (userInfo?.talent) {
    role = "Talent";
  } else if (userInfo?.recruiter?.is_corporate) {
    role = "Enterprise";
  } else if (userInfo?.recruiter) {
    role = "Recruiter";
  }

  const showLogoutModal = () => {
    setIsProfileDropdownOpen(false);
    setLogoutModalVisible(true);
  };

  const handleLogout = () => {
    const currentType = userInfo?.recruiter ? "recruiter" : "talent";

    localStorage.setItem("lastAccountType", currentType);

    signOut();
    localStorage.removeItem("userInfo");
    setLogoutModalVisible(false);

    navigate(`/sign-in/${currentType}`, { replace: true });
  };

  const handleSubmit = (data: any) => {
    const props: ISwitchTeamsProps = {
      team_id: data.id,
      token,
    };

    mutate(props, {
      onError: (err: any) => {
        openNotification({
          title: "Error",
          state: "error",
          description: err.response.data.msg,
          duration: 8.0,
        });
      },
      onSuccess: (res: any) => {
        const result = res.data;
        openNotification({
          title: "Success",
          state: "success",
          description: "Switched successfully",
          duration: 4.5,
        });
        setTimeout(() => {
          window.location.reload();
        }, 500);
      },
    });
  };

  const popNotification = (notification: any) => {
    setNotData(notification.data);

    const subject = String(notification?.data?.subject ?? "");
    const rawMessage = notification?.data?.message;

    const messageType: string | undefined =
      typeof rawMessage === "object" &&
      rawMessage !== null &&
      typeof (rawMessage as { type?: string }).type === "string"
        ? (rawMessage as { type: string }).type
        : undefined;

    const messageTextForLink = (() => {
      if (typeof rawMessage === "string") return rawMessage;
      if (
        typeof rawMessage === "object" &&
        rawMessage !== null &&
        typeof (rawMessage as { text?: string }).text === "string"
      ) {
        return (rawMessage as { text: string }).text;
      }
      return "";
    })();

    if (subject.includes("Team invite from")) {
      const link = extractLinkFromMessage(messageTextForLink);
      if (link) {
        window.location.href = link;
        markNotificationAsRead(notification);
        return;
      }
    }

    switch (messageType) {
      case "interview_invite":
        navigate(
          isTalent
            ? `${routePath.invites}/pending`
            : `${routePath.recruierInvites}/pending`,
        );
        break;
      case "interview_accepted":
        navigate(`${routePath.recruierInvites}/accepted`);
        break;
      case "interview_declined":
        navigate(`${routePath.recruierInvites}/rejected`);
        break;
      case "job_offer":
        navigate(
          isTalent
            ? `${routePath.invites}/offers`
            : `${routePath.recruierInvites}/pending`,
        );
        break;
      case "confirm_job_offer":
        navigate(`${routePath.recruierInvites}/accepted`);
        break;
      case "message":
        navigate(`/messages/${notification.data.message_id}`);
        break;
      case "task-assignment":
        navigate(`/tasks/${notification.data.task_id}`);
        break;
      default: {
        const link = extractLinkFromMessage(messageTextForLink);
        if (link) {
          window.location.href = link;
        }
        break;
      }
    }

    markNotificationAsRead(notification);
  };

  const canViewPricing = !(
    userInfo?.recruiter?.parent_id != null ||
    userInfo?.current_team !== undefined
  );

  const readNotificationsCount = notifications?.filter((notification: any) => {
    return notification.read_at === null;
  }).length;

  const profileDropdownContent = (
    <div className="bg-white rounded-xl shadow-lg w-56 py-2">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
        <img
          src={userInfo?.photo ?? avatar}
          alt="avatar"
          className="h-10 w-10 rounded-full"
        />
        <div className="min-w-0 max-w-[160px]">
          <p className="text-sm font-medium text-gray-900 break-words whitespace-normal">
            {userInfo?.first_name} {userInfo?.last_name}
          </p>

          <p className="text-xs text-gray-500 break-words whitespace-normal">
            {userInfo?.email}
          </p>
        </div>
      </div>

      <div
        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition"
        onClick={showLogoutModal}
      >
        <i className="ri-logout-box-r-line text-red-500 text-lg"></i>
        <span className="text-sm text-gray-700">Log out</span>
      </div>
    </div>
  );

  return (
    <>
      {/*<OpenNotification open={openNot} data={notData} handleClose={() => setOpenNot(false)} />*/}
      <ReviewForm
        open={reviewOpen}
        handleClose={() => {
          setReviewOpen(false);
        }}
        data={feedback}
      />
      <div className="w-full py-3 mb-3 flex justify-between items-center top-0 z-10">
        <i
          className="ri-menu-line lg:hidden text-xl cursor-pointer"
          onClick={() => setOpen(true)}
        ></i>
        <div className="lg:flex hidden">
          <div>
            <h1 className="font-bold text-xl job-board-title">{title}</h1>
            <h3 className="text-sm">{description}</h3>
          </div>
        </div>

        <div className="flex items-center gap-x-4">
          {isRecruiter && canViewPricing && (
            <button
              onClick={() => navigate(routePath.pricing)}
              className="flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 cursor-pointer transition-all duration-200 hover:bg-primary hover:border-primary hover:text-white hover:shadow-md"
            >
              <i className="ri-price-tag-3-line text-base"></i>
              <span>Pricing</span>
            </button>
          )}
          {teams && teams?.length > 0 && (
            <Dropdown
              trigger={["click"]}
              overlay={
                <div className="flex flex-col py-2 px-2 bg-white shadow-xl justify-start items-start min-w-[200px] rounded-lg">
                  {userInfo.current_team && (
                    <div
                      className="cursor-pointer px-2 py-1 hover:bg-gray-200 w-full rounded text-red-400"
                      onClick={() => handleSubmit({ id: "false" })}
                    >
                      Sign out of {userInfo.current_team.name}
                    </div>
                  )}
                  {teams?.map((team, i) => (
                    <div
                      className="py-1 px-2 cursor-pointer hover:bg-gray-200 w-full rounded"
                      key={i}
                      onClick={() => handleSubmit(team)}
                    >
                      {team.name}
                    </div>
                  ))}
                </div>
              }
            >
              <p className="flex justify-center items-center">
                <i className="ri-arrow-down-s-line text-2xl cursor-pointer"></i>
                <span className="text-sm">
                  {userInfo?.current_team
                    ? userInfo.current_team.name
                    : "Switch profile"}
                </span>
              </p>
            </Dropdown>
          )}
          <Dropdown
            trigger={["click"]}
            overlay={
              <div className="bg-white rounded-lg shadow-xl w-80 max-h-96 flex flex-col">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                  <h3 className="text-base font-semibold text-gray-900">
                    Notifications
                  </h3>
                  {readNotificationsCount > 0 && (
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                      disabled={isMarkingAllRead}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAllAsRead();
                      }}
                    >
                      {isMarkingAllRead ? "Marking..." : "Mark all as read"}
                    </button>
                  )}
                </div>
                <div className="overflow-y-auto">
                  {notifications && notifications.length > 0 ? (
                    notifications
                      .slice()
                      .sort((a: any, b: any) => {
                        if (a.read_at === null && b.read_at !== null) return -1;
                        if (a.read_at !== null && b.read_at === null) return 1;

                        return (
                          new Date(b.created_at).getTime() -
                          new Date(a.created_at).getTime()
                        );
                      })
                      .map((notification: any, index: number) => {
                        let preview = "";

                        if (typeof notification.data.message === "string") {
                          preview = notification.data.message;
                        } else if (notification.data.message?.text) {
                          preview = notification.data.message.text;
                        }

                        preview = preview
                          .replace(/<[^>]*>/g, "")
                          .substring(0, 50);
                        if (preview.length >= 50) preview += "...";

                        return (
                          <div
                            key={index}
                            className={`border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors ${
                              notification.read_at ? "bg-white" : "bg-blue-50"
                            }`}
                            onClick={() => popNotification(notification)}
                          >
                            <div className="flex p-4">
                              <div className="mr-3 flex-shrink-0 mt-0.5">
                                {notification.read_at ? (
                                  <div className="h-2 w-2 rounded-full bg-gray-300"></div>
                                ) : (
                                  <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p
                                  className={`text-sm font-medium ${
                                    notification.read_at
                                      ? "text-gray-900"
                                      : "text-gray-900 font-semibold"
                                  }`}
                                >
                                  {notification.data.subject}
                                </p>
                                {preview && (
                                  <p className="mt-1 text-sm text-gray-500 truncate">
                                    {preview}
                                  </p>
                                )}
                                <div className="mt-1 text-xs text-gray-400">
                                  {dayjs(notification.created_at).format(
                                    "DD/MM/YYYY",
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <div className="py-8 flex flex-col items-center justify-center">
                      <i className="ri-notification-off-line text-3xl text-gray-300 mb-2"></i>
                      <p className="text-gray-500 text-sm">No notifications</p>
                    </div>
                  )}
                </div>
              </div>
            }
          >
            <div className="relative cursor-pointer p-1">
              <i className="ri-notification-3-line text-xl"></i>
              {readNotificationsCount > 0 && (
                <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full transform translate-x-1/2 -translate-y-1/2">
                  {readNotificationsCount}
                </span>
              )}
            </div>
          </Dropdown>

          <Dropdown
            trigger={["click"]}
            open={isProfileDropdownOpen}
            onOpenChange={(open) => setIsProfileDropdownOpen(open)}
            dropdownRender={() => profileDropdownContent}
          >
            <div className="flex items-center gap-1 cursor-pointer px-2 py-1 rounded-lg hover:bg-gray-100 transition">
              <div className="flex flex-col items-center min-w-[72px]">
                <img
                  src={userInfo?.photo ?? avatar}
                  alt="avatar"
                  className="h-9 w-9 rounded-full object-cover"
                />

                <span className="mt-1 text-[11px] text-gray-600 font-medium text-center max-w-[90px] truncate">
                  {userInfo?.first_name || userInfo?.last_name
                    ? `${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`.trim()
                    : role}
                </span>
              </div>

              <i
                className={`ri-arrow-down-s-line text-lg text-gray-500 transition-transform ${
                  isProfileDropdownOpen ? "rotate-180" : ""
                }`}
              ></i>
            </div>
          </Dropdown>
        </div>
      </div>

      {/* Conditional Modal for Talent */}
      {isTalent && isLogoutModalVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 text-center relative">
              <button
                onClick={() => setLogoutModalVisible(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>

              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="text-red-500 text-2xl">⚠️</div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Sign out
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you would like to sign out of your Arno account?
              </p>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setLogoutModalVisible(false)}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  No
                </button>
                <button
                  onClick={handleLogout}
                  className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Yes, Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conditional Modal for Recruiter/Enterprise */}
      {(isRecruiter || userInfo?.recruiter?.is_corporate) && (
        // <Modal
        //   title="Confirm Logout"
        //   open={isLogoutModalVisible}
        //   onOk={handleLogout}
        //   onCancel={() => setLogoutModalVisible(false)}
        //   okText="Logout"
        //   cancelText="Cancel"
        // >
        //   <p>Are you sure you want to log out?</p>
        // </Modal>

        <WarningModal
          open={isLogoutModalVisible}
          title="Sign out"
          content={
            <>
              Are you sure you would like to sign <br /> out of your Arno
              account?
            </>
          }
          onConfirm={handleLogout}
          onCancel={() => setLogoutModalVisible(false)}
          confirmText="Yes, Sign Out"
          cancelText="No"
        />
      )}

      {/* Drawer for mobile navbar */}
      <Drawer
        placement="right"
        onClose={() => setOpen(false)}
        open={open}
        style={{ background: "var(--app-color-primary)" }}
      >
        <SideBar startTour={handleStartTour} />
      </Drawer>
    </>
  );
};
